import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Check,Zap ,  Users, Clock, ShieldCheck, ChevronDown, X as XIcon } from "lucide-react";
import { toast } from "react-toastify";
import apiClient from "../common/apiClient";
import { ADDONS } from "../constants/feature.constants";

const TAX_RATE = 0.18;

// Duration Popup Component
const DurationPopup = ({ isOpen, onClose, selected, onSelect }) => {
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
			<div className="bg-white rounded-2xl w-full max-w-[340px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
				<div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
					<h4 className="font-bold text-gray-900 flex items-center gap-2">
						<Clock size={18} className="text-[#503691]" />
						Select Duration
					</h4>
					<button onClick={onClose} className="p-1 hover:bg-gray-200 rounded-full transition-colors">
						<XIcon size={18} className="text-gray-500" />
					</button>
				</div>
				<div className="p-3 grid grid-cols-3 gap-2">
					{Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
						<button
							key={month}
							onClick={() => {
								onSelect(month);
								onClose();
							}}
							className={`py-3 px-2 rounded-xl text-sm font-bold transition-all border-2 ${
								selected === month
									? "bg-[#503691] border-[#503691] text-white shadow-md shadow-[#503691]/20"
									: "bg-white border-gray-100 text-gray-600 hover:border-[#503691]/30 hover:bg-gray-50"
							}`}
						>
							{month} {month === 1 ? "Mo" : "Mos"}
						</button>
					))}
				</div>
			</div>
		</div>
	);
};

const loadRazorpayScript = () => {
	return new Promise((resolve) => {
		const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
		if (existing) {
			resolve(true);
			return;
		}

		const script = document.createElement("script");
		script.src = "https://checkout.razorpay.com/v1/checkout.js";
		script.onload = () => resolve(true);
		script.onerror = () => resolve(false);
		document.body.appendChild(script);
	});
};

const normalize = (value) => String(value || "").trim().toLowerCase();
const getAddonMonthlyContactLimit = (addon) => Number(addon?.TotalContactLimit ?? addon?.totalContactLimit ?? addon?.value ?? 0);
const getAddonAddCredits = (addon) => Number(addon?.AddContactCredits ?? addon?.addContactCredits ?? 0);
const getCreditsSourceCode = (addon) => {
	let source = addon?.CreditsSource ?? addon?.creditsSource ?? addon?.CreditSource ?? addon?.creditsource ?? addon?.source;
	
	// Handle if CreditsSource is an array
	if (Array.isArray(source) && source.length > 0) {
		source = source[0];
	}
	
	return normalize(source);
};
const mapCreditsSourceToContactLimitField = (creditsSourceCode) => {
	const normalizedCode = normalize(creditsSourceCode);
	
	// Recognize numeric codes: owner1010, builder1010, broker1010
	if (normalizedCode === "owner1010" || normalizedCode === normalize(ADDONS.OWNER_PROPERTY)) {
		return { field: "OwnerTotalContactLimit", title: "Owner Property Contact Limit" };
	}
	
	if (normalizedCode === "builder1010" || normalizedCode === normalize(ADDONS.BUILDER_PROPERTY)) {
		return { field: "BuilderTotalContactLimit", title: "Builder Property Contact Limit" };
	}
	
	if (normalizedCode === "broker1010" || normalizedCode === normalize(ADDONS.BROKER_PROPERTY)) {
		return { field: "BrokerTotalContactLimit", title: "Broker Property Contact Limit" };
	}
	
	return { field: null, title: "Contact Limit" };
};
const getContactLimitTitle = (codeOrAddon) => {
	const code = typeof codeOrAddon === "string" ? codeOrAddon : getCreditsSourceCode(codeOrAddon);
	const { title } = mapCreditsSourceToContactLimitField(code);
	return title;
};
const isDurationBasedAddonCode = (code) => {
	const normalizedCode = normalize(code);
	return [
		normalize(ADDONS.OWNER_PROPERTY),
		normalize(ADDONS.BUILDER_PROPERTY),
		normalize(ADDONS.BROKER_PROPERTY),
	].includes(normalizedCode);
};
const getFeatureContactLimitPayload = (addonCode, scaledLimit) => {
	const normalizedCode = normalize(addonCode);
	const normalizedOwnerCode = normalize(ADDONS.OWNER_PROPERTY);
	const normalizedBuilderCode = normalize(ADDONS.BUILDER_PROPERTY);
	const normalizedBrokerCode = normalize(ADDONS.BROKER_PROPERTY);
	const contactLimitValue = Number(scaledLimit || 0);

	if (!contactLimitValue) {
		return {};
	}

	if (normalizedCode === normalizedBuilderCode) {
		return { BuilderTotalContactLimit: contactLimitValue };
	}

	if (normalizedCode === normalizedOwnerCode) {
		return { OwnerTotalContactLimit: contactLimitValue };
	}

	if (normalizedCode === normalizedBrokerCode || normalizedCode === "broker100") {
		return {
			BrokerTotalContactLimit: contactLimitValue,
			RequirementTotalContactLimit: contactLimitValue,
		};
	}

	return {};
};
const getCreditAddonPayload = (addon, activePlan) => {
	const creditsSourceCode = getCreditsSourceCode(addon);
	const addCredits = getAddonAddCredits(addon);
	const currentOwnerLimit = Number(activePlan?.OwnerTotalContactLimit ?? activePlan?.ownerTotalContactLimit ?? 0);
	const currentBuilderLimit = Number(activePlan?.BuilderTotalContactLimit ?? activePlan?.builderTotalContactLimit ?? 0);
	const currentBrokerLimit = Number(activePlan?.BrokerTotalContactLimit ?? activePlan?.brokerTotalContactLimit ?? 0);
	const currentRequirementLimit = Number(activePlan?.RequirementTotalContactLimit ?? activePlan?.requirementTotalContactLimit ?? 0);

	if (!creditsSourceCode || !addCredits) {
		return {};
	}

	const { field } = mapCreditsSourceToContactLimitField(creditsSourceCode);
	
	if (field === "OwnerTotalContactLimit") {
		return {
			OwnerTotalContactLimit: currentOwnerLimit + addCredits,
		};
	}

	if (field === "BuilderTotalContactLimit") {
		return {
			BuilderTotalContactLimit: currentBuilderLimit + addCredits,
		};
	}

	if (field === "BrokerTotalContactLimit") {
		return {
			BrokerTotalContactLimit: currentBrokerLimit + addCredits,
			RequirementTotalContactLimit: currentRequirementLimit + addCredits,
		};
	}

	return {};
};
const formatMetricValue = (value) => Number(value || 0).toLocaleString("en-IN");
const formatCurrency = (value) => `₹${Number(value || 0).toFixed(2)}`;
const toPreviousDayIso = (dateValue) => {
	if (!dateValue) return "";

	const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
	if (Number.isNaN(date.getTime())) return "";

	const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
	utcDate.setUTCDate(utcDate.getUTCDate() - 1);
	return utcDate.toISOString();
};
const getAddonCouponList = (addon) => {
	if (Array.isArray(addon?.promoCodes)) return addon.promoCodes;
	if (Array.isArray(addon?.PromoCodes)) return addon.PromoCodes;
	return [];
};
const getCouponCode = (coupon) => coupon?.code ?? coupon?.Code ?? "";
const getCouponDiscountType = (coupon) => coupon?.discountType ?? coupon?.DiscountType ?? "";
const getCouponDiscountValue = (coupon) => Number(coupon?.discountValue ?? coupon?.DiscountValue ?? 0);
const getCouponStatus = (coupon) => normalize(coupon?.status ?? coupon?.Status ?? "");
const isCouponActive = (coupon) => {
	const status = getCouponStatus(coupon);
	const isActiveField = coupon?.isActive ?? coupon?.IsActive;

	// If status is explicitly 'active', we treat it as active regardless of isActive boolean field
	if (status === "active") return true;
	
	// Fallback to isActive boolean if status is missing or something else
	return isActiveField === true;
};
const normalizeAddonStatus = (statusValue) => {
	const status = normalize(statusValue);
	if (status === "pending") return "pending";
	if (status === "active") return "active";
	return "";
};
const getAddonStatusBadge = (statusValue) => {
	const status = normalizeAddonStatus(statusValue);

	if (status === "pending") {
		return {
			label: "Future Add-ons",
			className: "bg-amber-50 text-amber-700 border border-amber-200",
		};
	}

	if (status === "active") {
		return {
			label: "Active",
			className: "bg-emerald-50 text-emerald-700 border border-emerald-200",
		};
	}

	return null;
};
const buildAddonCheckoutPricing = ({ addon, selectedLimitValue, selectedDurationMonths, isCreditAddon }) => {
	const addonType = normalize(addon?.type);
	const multiplier = addonType === "limit" ? selectedLimitValue : (isCreditAddon ? 1 : selectedDurationMonths);
	const originalPrice = Number(addon?.price || 0) * Number(multiplier || 1);

	return {
		originalPrice,
	};
};
const getCouponDiscountAmount = (coupon, originalPrice) => {
	if (!coupon) return 0;

	if (normalize(getCouponDiscountType(coupon)) === "percentage") {
		return Number(
			((Number(originalPrice || 0) * getCouponDiscountValue(coupon)) / 100).toFixed(2)
		);
	}

	return Number(getCouponDiscountValue(coupon).toFixed(2));
};
const buildAddonSuccessSummary = ({
	addon,
	amount,
	accessKey,
	selectedLimitValue,
	selectedDurationMonths,
	currentSubUserLimit,
	isDurationBasedAddon,
	isCreditAddon,
	creditAddonPayload,
	scaledTotalContactLimit,
}) => {
	const creditsSourceCode = getCreditsSourceCode(addon);
	const { field, title } = mapCreditsSourceToContactLimitField(creditsSourceCode);
	const totalSubUsers = currentSubUserLimit + selectedLimitValue;
	const contactLimitValue = field ? Number(creditAddonPayload?.[field] || 0) : 0;

	let metricCard = {
		label: "Status",
		value: "Active",
		meta: "Add-on enabled",
	};

	if (accessKey === "SUBUSER_LIMIT") {
		metricCard = {
			label: "Sub User Limit",
			value: String(totalSubUsers),
			meta: `${selectedLimitValue} added`,
		};
	} else if (isCreditAddon) {
		metricCard = {
			label: "Contact Credits",
			value: formatMetricValue(contactLimitValue),
			meta: title || "Contact limit updated",
		};
	} else if (isDurationBasedAddon) {
		metricCard = {
			label: "Duration",
			value: String(selectedDurationMonths),
			meta: `${selectedDurationMonths} month${selectedDurationMonths > 1 ? "s" : ""}`,
		};
	} else if (scaledTotalContactLimit > 0) {
		metricCard = {
			label: "Contact Limit",
			value: formatMetricValue(scaledTotalContactLimit),
			meta: getContactLimitTitle(addon),
		};
	}

	return {
		paymentType: "addon",
		title: "Your add-on is active",
		description: "The payment was processed successfully and your account has been updated with the selected add-on access.",
		amount,
		addonName: addon?.name || "Add-on",
		accessItems: [addon?.name].filter(Boolean),
		infoCards: [
			{
				label: "Amount Paid",
				value: `₹${formatMetricValue(amount)}`,
			},
			{
				label: "Add-on",
				value: addon?.name || "Add-on",
			},
			metricCard,
		],
	};
};

const Addons = ({
	userId,
	token,
	activePlan,
	isDemoAccount,
	fallbackPaidAccountId,
	fallbackSubUserLimit = 0,
	onPaymentSuccess,
	paidAddonsData = [],
}) => {
	const [loading, setLoading] = useState(false);
	const [addons, setAddons] = useState([]);
	const [extraSubUsers, setExtraSubUsers] = useState(1);
	const [isSubUserSelected, setIsSubUserSelected] = useState(false);
	const [processingAddonId, setProcessingAddonId] = useState(null);
	const [featureDurations, setFeatureDurations] = useState({});
	const [activeDurationPopup, setActiveDurationPopup] = useState(null); // ID of addon with open popup
	const [checkoutConfig, setCheckoutConfig] = useState(null);
	const [checkoutCoupon, setCheckoutCoupon] = useState("");
	const [appliedCheckoutCoupon, setAppliedCheckoutCoupon] = useState(null);
	const paidAccountId = activePlan?._id || fallbackPaidAccountId;
	const currentSubUserLimit = Math.max(
		Number(activePlan?.subUserLimit || 0),
		Number(fallbackSubUserLimit || 0)
	);

	useEffect(() => {
		const fetchAddons = async () => {
			if (!userId) return;

			try {
				setLoading(true);
				const response = await apiClient(
					"get",
					`/ijscui/probroker-admin/ceknvuhbd/PremiumPlan/protected/addons/user/${userId}`,
					{},
					{ headers: { Authorization: `Bearer ${token}` } }
				);

				if (response.success) {
					setAddons(response.data?.data?.docs || []);
					return;
				}

				toast.error(response.error?.message || "Failed to fetch addons");
			} catch {
				toast.error("An error occurred while fetching addons");
			} finally {
				setLoading(false);
			}
		};

		fetchAddons();
	}, [userId, token]);

	const resolveAddonAccessKey = (addon) => {
		const addonCode = normalize(addon?.code);

		const matchedEntry = Object.entries(ADDONS).find(([, code]) => {
			const normalizedCode = normalize(code);
			if (!normalizedCode) return false;
			return normalizedCode === addonCode;
		});

		if (matchedEntry) return matchedEntry[0];

		return null;
	};

	const mappedAddons = useMemo(() => {
		return addons
			.map((addon) => {
				const accessKey = resolveAddonAccessKey(addon);
				if (!accessKey) return null;

				return {
					addon,
					accessKey,
					isSubUser: accessKey === "SUBUSER_LIMIT",
				};
			})
			.filter(Boolean);
	}, [addons]);

	const addonStatusByCode = useMemo(() => {
		const sortedHistory = Array.isArray(paidAddonsData)
			? [...paidAddonsData].sort((a, b) => new Date(b?.createdOn || b?.createdAt || b?.StartDate || 0) - new Date(a?.createdOn || a?.createdAt || a?.StartDate || 0))
			: [];
		const statusMap = new Map();

		sortedHistory.forEach((entry) => {
			const code = normalize(entry?.AddonAcces || entry?.code || entry?.addonCode);
			if (!code || statusMap.has(code)) return;
			statusMap.set(code, normalizeAddonStatus(entry?.AddonStatus || entry?.status));
		});

		return statusMap;
	}, [paidAddonsData]);

	const getExistingAddonStatus = (addonCode) => addonStatusByCode.get(normalize(addonCode)) || "";

	const startAddonCheckout = ({ addon, accessKey, value = 1, durationMonths = 1 }) => {
		if (!addon || !accessKey) return;

		const addonType = normalize(addon.type);
		const selectedLimitValue = Number(value || 1);
		const selectedDurationMonths = Number(durationMonths || 1);
		const isDurationBasedAddon = addonType === "feature" && isDurationBasedAddonCode(addon?.code);
		const isCreditAddon = normalize(addon?.code) === normalize(ADDONS.CONATCT_CREDIT);
		const baseTotalContactLimit = getAddonMonthlyContactLimit(addon);
		const scaledTotalContactLimit = addonType === "feature"
			? (isDurationBasedAddon ? baseTotalContactLimit * selectedDurationMonths : baseTotalContactLimit)
			: baseTotalContactLimit;

		const { originalPrice } = buildAddonCheckoutPricing({
			addon,
			selectedLimitValue,
			selectedDurationMonths,
			isCreditAddon,
		});

		if (originalPrice <= 0) {
			toast.error("Invalid add-on amount");
			return;
		}

		setCheckoutConfig({
			addon,
			accessKey,
			value: selectedLimitValue,
			durationMonths: selectedDurationMonths,
			addonType,
			isDurationBasedAddon,
			isCreditAddon,
			baseTotalContactLimit,
			scaledTotalContactLimit,
			originalPrice,
		});
		setCheckoutCoupon("");
		setAppliedCheckoutCoupon(null);
	};

	const handleApplyAddonCoupon = () => {
		if (!checkoutConfig?.addon || !checkoutConfig.originalPrice) return;
		if (!checkoutCoupon.trim()) {
			toast.error("Please enter coupon code");
			return;
		}

		const availableCoupons = getAddonCouponList(checkoutConfig.addon);
		if (!availableCoupons.length) {
			toast.error("No coupons available for this add-on");
			return;
		}

		const matchedCoupon = availableCoupons.find(
			(coupon) => normalize(getCouponCode(coupon)) === normalize(checkoutCoupon)
		);

		if (!matchedCoupon) {
			toast.error("Invalid coupon code");
			return;
		}

		// 1. Check Status
		const status = getCouponStatus(matchedCoupon);
		if (status === "expired") {
			toast.error("This coupon code has expired");
			return;
		}
		if (!isCouponActive(matchedCoupon)) {
			toast.error("This coupon code is currently not active");
			return;
		}

		// 2. Check Dates
		const now = new Date();
		const startDate = matchedCoupon?.startDate ? new Date(matchedCoupon.startDate) : null;
		const expiryDate = matchedCoupon?.expiryDate ? new Date(matchedCoupon.expiryDate) : null;

		if (startDate && now < startDate) {
			toast.error("This coupon is not yet valid");
			return;
		}
		if (expiryDate && now > expiryDate) {
			toast.error("This coupon has expired");
			return;
		}

		// 3. Check Minimum Purchase Amount
		const minAmount = Number(matchedCoupon?.minPurchaseAmount ?? matchedCoupon?.MinPurchaseAmount ?? 0);
		if (checkoutConfig.originalPrice < minAmount) {
			toast.error(`This coupon requires a minimum purchase of ₹${minAmount}`);
			return;
		}

		setAppliedCheckoutCoupon(matchedCoupon);
		toast.success("Coupon applied successfully!");
	};

	const handleCancelAddonCoupon = () => {
		setCheckoutCoupon("");
		setAppliedCheckoutCoupon(null);
		toast.info("Coupon removed");
	};

	const handleAddonPayment = async ({ addon, accessKey, value = 1, durationMonths = 1 }) => {
		if (!addon || !accessKey || !userId) return;

		const addonType = normalize(addon.type);
		const selectedLimitValue = Number(value || 1);
		const selectedDurationMonths = Number(durationMonths || 1);
		const isDurationBasedAddon = addonType === "feature" && isDurationBasedAddonCode(addon?.code);
		const isCreditAddon = normalize(addon?.code) === normalize(ADDONS.CONATCT_CREDIT);
		const { originalPrice } = buildAddonCheckoutPricing({
			addon,
			selectedLimitValue,
			selectedDurationMonths,
			isCreditAddon,
		});
		const discountAmountRaw = getCouponDiscountAmount(appliedCheckoutCoupon, originalPrice);
		const subtotalAfterDiscount = originalPrice - discountAmountRaw;
		const taxAmountRaw = subtotalAfterDiscount * TAX_RATE;
		const discountAmount = Number(discountAmountRaw.toFixed(2));
		const taxAmount = Number(taxAmountRaw.toFixed(2));		
		const finalAmount = Number((subtotalAfterDiscount + taxAmountRaw).toFixed(2));
		const baseTotalContactLimit = getAddonMonthlyContactLimit(addon);
		const scaledTotalContactLimit = addonType === "feature"
			? (isDurationBasedAddonCode(addon?.code) ? baseTotalContactLimit * selectedDurationMonths : baseTotalContactLimit)
			: baseTotalContactLimit;
		const featureContactLimitPayload = addonType === "feature"
			? getFeatureContactLimitPayload(addon?.code, scaledTotalContactLimit)
			: {};
		const creditAddonPayload = isCreditAddon ? getCreditAddonPayload(addon, activePlan) : {};
		if (finalAmount <= 0) {
			toast.error("Invalid add-on amount");
			return;
		}

		const isLoaded = await loadRazorpayScript();
		if (!isLoaded) {
			toast.error("Razorpay SDK failed to load. Are you online?");
			return;
		}

		try {
			setProcessingAddonId(addon._id);
			const now = new Date();
			const endDate = new Date(now);
			if (addonType === "feature") {
				if (isDurationBasedAddon) {
					endDate.setMonth(endDate.getMonth() + selectedDurationMonths);
				}
			} else {
				endDate.setDate(endDate.getDate() + Number(addon.durationDays || 30));
			}
			const subUserLimit = accessKey === "SUBUSER_LIMIT"
				? currentSubUserLimit + selectedLimitValue
				: currentSubUserLimit;

			const orderResponse = await axios.post(
				`${process.env.REACT_APP_API_IP || ""}/ijscui/probroker-admin/ceknvuhbd/PremiumPlan/protected/payment/order`,
				{
					amount: Number(finalAmount.toFixed(2)),
					currency: "INR",
					subUserLimit,
					addonId: addon._id,
				}
			);

			if (!orderResponse.data?.order?.id) {
				toast.error("Failed to create Razorpay order");
				return;
			}

			if (!process.env.REACT_APP_RAZORPAY_KEY_ID || process.env.REACT_APP_RAZORPAY_KEY_ID === "YOUR_RAZORPAY_KEY_ID") {
				toast.error("Razorpay key is missing or not configured.");
				return;
			}

			const options = {
				key: process.env.REACT_APP_RAZORPAY_KEY_ID,
				amount: Math.round(finalAmount * 100),
				currency: "INR",
				name: "PRObroker",
				description: `Purchase ${addon.name}`,
				order_id: orderResponse.data.order.id,
				notes: {
					addonId: addon._id,
					userId,
					subUserLimit,
						value: addonType === "feature" ? accessKey : selectedLimitValue,
							durationMonths: isDurationBasedAddon ? selectedDurationMonths : undefined,
					accessKey,
				},
				handler: async (response) => {
					try {
						const preciseOriginalPrice = Number((Number(originalPrice || 0)).toFixed(2));
						const preciseTaxAmount = Number(taxAmountRaw.toFixed(2));
						const precisDiscountAmount = Number(discountAmountRaw.toFixed(2));
						const preciseFinalAmount = Number((subtotalAfterDiscount + taxAmountRaw).toFixed(2));
						const activatePayload = {
							userId,
							...(paidAccountId ? { PaidAccountId: paidAccountId } : {}),
							AddonAcces: addon.code,
							name: addon.name,
							AddonName: addon.name,
							Amount: String(preciseFinalAmount),
							amount: preciseFinalAmount,
							originalPrice: preciseOriginalPrice,
							taxAmount: preciseTaxAmount,
							discountAmount: precisDiscountAmount,
							promocode: appliedCheckoutCoupon?.code || "",
							Value: addonType === "feature" ? accessKey : String(selectedLimitValue),
							DurationMonths: isDurationBasedAddon ? selectedDurationMonths : undefined,
							Duration: isDurationBasedAddon ? String(selectedDurationMonths) : undefined,
							AddContactCredits: getAddonAddCredits(addon),
							razorpayOrderId: response.razorpay_order_id,
							razorpayPaymentId: response.razorpay_payment_id,
							razorpaySignature: response.razorpay_signature,
							StartDate: now.toISOString(),
							EndDate: toPreviousDayIso(endDate),
							OwnerTotalContactLimit: activePlan?.OwnerTotalContactLimit,
							BuilderTotalContactLimit: activePlan?.BuilderTotalContactLimit,
							BrokerTotalContactLimit: activePlan?.BrokerTotalContactLimit,
							RequirementTotalContactLimit: activePlan?.RequirementTotalContactLimit,
							TotalContactLimit: scaledTotalContactLimit,
							...featureContactLimitPayload,
							...creditAddonPayload,
							PlanData: activePlan,
						};

						const activationResult = await apiClient(
							"post",
							"/ijscui/probroker-admin/ceknvuhbd/PremiumPlan/protected/addons/active",
							activatePayload,
							{ headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
						);

						if (!activationResult.success) {
							toast.error(activationResult.error?.message || "Addon activation failed after payment");
							return;
						}

						toast.success("Add-on activated successfully");
						setCheckoutConfig(null);
						setCheckoutCoupon("");
						setAppliedCheckoutCoupon(null);
						if (typeof onPaymentSuccess === "function") {
							onPaymentSuccess(
								buildAddonSuccessSummary({
									addon,
									amount: finalAmount,
									accessKey,
									selectedLimitValue,
									selectedDurationMonths,
									currentSubUserLimit,
									isDurationBasedAddon,
									isCreditAddon,
									creditAddonPayload,
									scaledTotalContactLimit,
								})
							);
						}
					} catch {
						toast.error("Payment success but addon activation failed");
					}
				},
				prefill: { name: "", email: "", contact: "" },
				theme: { color: "#7c3aed" },
			};

			const rzp = new window.Razorpay(options);
			rzp.open();
		} catch {
			toast.error("Something went wrong with add-on payment");
		} finally {
			setProcessingAddonId(null);
		}
	};

	const checkoutPricing = useMemo(() => {
		if (!checkoutConfig) {
			return {
				discountAmount: 0,
				taxAmount: 0,
				finalAmount: 0,
			};
		}

		const discountAmountRaw = getCouponDiscountAmount(appliedCheckoutCoupon, checkoutConfig.originalPrice);
		const subtotalAfterDiscount = checkoutConfig.originalPrice - discountAmountRaw;
		const taxAmountRaw = subtotalAfterDiscount * TAX_RATE;
		const discountAmount = Number(discountAmountRaw.toFixed(2));
		const taxAmount = Number(taxAmountRaw.toFixed(2));
		const finalAmount = Number((subtotalAfterDiscount + taxAmountRaw).toFixed(2));

		return {
			discountAmount,
			taxAmount,
			finalAmount,
		};
	}, [checkoutConfig, appliedCheckoutCoupon]);

	if (loading) {
		return (
			<div className="max-w-3xl mx-auto bg-white rounded-xl border border-gray-100 p-3 shadow-sm text-center text-gray-500 text-sm font-semibold">
				Loading add-ons...
			</div>
		);
	}

	if (!mappedAddons.length) {
		return (
			<div className="max-w-3xl mx-auto bg-white rounded-xl border border-gray-100 p-3 shadow-sm text-center text-gray-500 text-sm font-semibold">
				No matching add-ons available for this account.
			</div>
		);
	}

	if (checkoutConfig) {
		const { addon, accessKey, value, durationMonths, isDurationBasedAddon, originalPrice } = checkoutConfig;
		const availableCoupons = getAddonCouponList(addon);

		return (
			<div className="max-w-3xl mx-auto space-y-4">
				<button
					type="button"
					onClick={() => {
						setCheckoutConfig(null);
						setCheckoutCoupon("");
						setAppliedCheckoutCoupon(null);
					}}
					className="text-sm font-bold text-[#503691] hover:text-[#503691]/70"
				>
					← Back to add-ons
				</button>

				<div className="rounded-2xl border border-[#503691]/20 bg-white p-4 md:p-5">
					<h3 className="text-xl font-bold text-gray-900">Features Checkout</h3>
					<p className="text-sm text-gray-500 mt-1">Review full feature details before payment.</p>
					

					<div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
						<label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Apply Coupon Code</label>
						<div className="mt-2 flex flex-col sm:flex-row gap-2">
							<input
								type="text"
								value={checkoutCoupon}
								onChange={(event) => {
									setCheckoutCoupon(event.target.value);
									if (appliedCheckoutCoupon && normalize(appliedCheckoutCoupon?.code) !== normalize(event.target.value)) {
										setAppliedCheckoutCoupon(null);
									}
								}}
								placeholder="Enter coupon code"
								className="flex-1 px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold"
							/>
							<button
								type="button"
								onClick={handleApplyAddonCoupon}
								className="px-4 py-2 rounded-lg bg-[#503691] hover:bg-[#503691]/70 text-white text-sm font-BOLD"
							>
								Apply
							</button>
							<button
								type="button"
								onClick={handleCancelAddonCoupon}
								disabled={!checkoutCoupon && !appliedCheckoutCoupon}
								className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 text-sm font-bold disabled:opacity-50"
							>
								Cancel
							</button>
						</div>
						{appliedCheckoutCoupon ? (
							<p className="mt-2 text-xs font-semibold text-emerald-700">
								Applied code: {getCouponCode(appliedCheckoutCoupon)}
							</p>
						) : null}
						
					</div>

					<div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/40 p-4 space-y-2">
						<div className="flex items-center justify-between text-sm font-semibold text-gray-600">
								<span>1. Original Price</span>
								<span>{formatCurrency(originalPrice)}</span>
							</div>
							{appliedCheckoutCoupon && (
								<div className="flex items-center justify-between text-sm font-semibold text-emerald-600">
									<span>2. Discount ({appliedCheckoutCoupon.discountValue}{appliedCheckoutCoupon.discountType === "percentage" ? "%" : " Flat"})</span>
									<span>-{formatCurrency(checkoutPricing.discountAmount)}</span>
								</div>
							)}
							<div className="flex items-center justify-between text-sm font-semibold text-gray-600">
								<span>{appliedCheckoutCoupon ? "3. Subtotal" : "2. Subtotal"}</span>
								<span>{formatCurrency(originalPrice - checkoutPricing.discountAmount)}</span>
							</div>
							<div className="flex items-center justify-between text-sm font-semibold text-gray-600">
								<span>{appliedCheckoutCoupon ? "4. GST (18%)" : "3. GST (18%)"}</span>
								<span>{formatCurrency(checkoutPricing.taxAmount)}</span>
							</div>
							<div className="border-t border-[#503691]/20 pt-2 flex items-center justify-between">
								<span className="text-base font-bold text-gray-900">{appliedCheckoutCoupon ? "5. Final Amount" : "4. Final Amount"}</span>
								<span className="text-2xl font-black text-[#503691]">{formatCurrency(checkoutPricing.finalAmount)}</span>
							</div>
					</div>

					<button
						type="button"
						onClick={() => handleAddonPayment({ addon, accessKey, value, durationMonths })}
						disabled={processingAddonId === addon?._id}
						className="mt-4 w-full sm:w-auto px-5 py-2.5 rounded-lg bg-[#503691] hover:bg-[#503691]/70 disabled:bg-[#503691]/30 text-white font-bold text-sm"
					>
						{processingAddonId === addon?._id ? "Processing..." : `Pay ${formatCurrency(checkoutPricing.finalAmount)}`}
					</button>
				</div>
			</div>
		);
	}

	const includedSubUsers = currentSubUserLimit;

	const renderSubUserCard = (addon, accessKey) => {
		const totalSubUsers = includedSubUsers + extraSubUsers;
		const addonCost = Number(addon.price || 0) * extraSubUsers;
		const statusBadge = getAddonStatusBadge(getExistingAddonStatus(addon?.code));
		
		const maxTotalAllowed = Number(addon.value || 100);
		const maxExtraAllowed = Math.max(0, maxTotalAllowed - includedSubUsers);

		return (
			<div
				key={addon._id}
				className={`relative rounded-3xl border-2 transition-all duration-300 flex flex-col h-full bg-white overflow-hidden ${
					isSubUserSelected
						? "border-[#503691] shadow-xl shadow-[#503691]/10"
						: "border-gray-100 hover:border-[#503691]/30 hover:shadow-lg"
				}`}
			>
				{/* Top Header */}
				<div className="p-5 border-b border-gray-50 flex-1">
					<div className="flex items-start justify-between mb-4">
						<div className="w-12 h-12 rounded-2xl bg-violet-50 text-[#503691] flex items-center justify-center">
							<Users size={24} />
						</div>
						{statusBadge && (
							<span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${statusBadge.className}`}>
								{statusBadge.label}
							</span>
						)}
					</div>
					
					<h3 className="text-xl font-black text-gray-900 leading-tight mb-2">{addon.name}</h3>
					<p className="text-sm text-gray-500 font-medium leading-relaxed mb-4">
						{addon.description || `Enhance your staff limit. Currently ${includedSubUsers} users included.`}
					</p>

					<div className="space-y-3">
						<div className="flex items-center gap-3">
							<div className="w-5 h-5 rounded-full bg-emerald-50 flex items-center justify-center">
								<Check size={12} className="text-emerald-600" strokeWidth={3} />
							</div>
							<span className="text-sm font-bold text-gray-600">
								{maxExtraAllowed > 0 ? `Max extra: ${maxExtraAllowed}` : "Max limit reached"}
							</span>
						</div>
						<div className="flex items-center gap-3">
							<div className="w-5 h-5 rounded-full bg-blue-50 flex items-center justify-center">
								<ShieldCheck size={12} className="text-blue-600" />
							</div>
							<span className="text-sm font-bold text-gray-600">
								Total limit: {maxTotalAllowed} users
							</span>
						</div>
					</div>
				</div>

				{/* Selection / Increment Section */}
				<div className={`px-5 py-4 bg-gray-50/50 transition-all duration-300 ${isSubUserSelected ? "block" : "hidden"}`}>
					<div className="flex items-center justify-between mb-3">
						<span className="text-xs font-black text-gray-400 uppercase tracking-widest">Quantity</span>
						<div className="flex items-center gap-3 bg-white p-1 rounded-xl shadow-sm border border-gray-100">
							<button
								onClick={(e) => {
									e.stopPropagation();
									setExtraSubUsers((prev) => Math.max(1, prev - 1));
								}}
								className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-50 transition-all text-xl font-bold"
							>
								-
							</button>
							<span className="w-6 text-center text-base font-black text-gray-900">{extraSubUsers}</span>
							<button
								onClick={(e) => {
									e.stopPropagation();
									if (extraSubUsers >= maxExtraAllowed) {
										toast.warning(`You can only add up to ${maxExtraAllowed} more users.`);
										return;
									}
									setExtraSubUsers((prev) => prev + 1);
								}}
								className="w-8 h-8 rounded-lg flex items-center justify-center text-[#503691] hover:bg-violet-50 transition-all text-xl font-bold"
							>
								+
							</button>
						</div>
					</div>
					<div className="flex items-center justify-between text-sm">
						<span className="font-bold text-gray-500">Total users</span>
						<span className="font-black text-gray-900">{totalSubUsers}</span>
					</div>
				</div>

				{/* Footer Price & Button */}
				<div className="p-5 bg-white border-t border-gray-50">
					<div className="flex items-end justify-between mb-4">
						<div>
							<p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Add-on Price</p>
							<div className="flex items-baseline gap-1">
								<span className="text-2xl font-black text-gray-900">₹{formatMetricValue(addonCost)}</span>
								<span className="text-xs font-bold text-gray-500">/total</span>
							</div>
						</div>
						{!isSubUserSelected && (
							<div className="text-right">
								<span className="text-lg font-black text-gray-900">₹{addon.price}</span>
								<p className="text-[10px] font-bold text-gray-400">per user</p>
							</div>
						)}
					</div>

					<button
						onClick={() => {
							if (!isSubUserSelected) {
								if (isDemoAccount) {
									toast.info("Sub-user add-ons are not available for Demo Accounts.");
									return;
								}
								if (maxExtraAllowed <= 0) {
									toast.info(`Maximum limit of ${maxTotalAllowed} reached.`);
									return;
								}
								setIsSubUserSelected(true);
							} else {
								startAddonCheckout({ addon, accessKey, value: extraSubUsers, durationMonths: 1 });
							}
						}}
						disabled={processingAddonId === addon._id}
						className={`w-full py-3.5 rounded-2xl font-black text-sm tracking-wide transition-all ${
							isSubUserSelected
								? "bg-[#503691] text-white shadow-lg shadow-[#503691]/20 hover:scale-[1.02]"
								: "bg-white border-2 border-[#503691] text-[#503691] hover:bg-[#503691]/5 shadow-sm"
						}`}
					>
						{isSubUserSelected ? (processingAddonId === addon._id ? "Processing..." : "Add Your Plan") : "Select Add-on"}
					</button>
				</div>
			</div>
		);
	};

	const renderNormalCard = (addon, accessKey) => {
		const selectedDurationMonths = Number(featureDurations[addon._id] || 1);
		const addonCode = normalize(addon?.code);
		const isCreditAddon = addonCode === normalize(ADDONS.CONATCT_CREDIT);
		const showDuration = isDurationBasedAddonCode(addonCode);
		const featureCost = Number(addon.price || 0) * (showDuration ? selectedDurationMonths : 1);
		const baseTotalContactLimit = getAddonMonthlyContactLimit(addon);
		const addCredits = getAddonAddCredits(addon);
		const scaledTotalContactLimit = showDuration ? baseTotalContactLimit * selectedDurationMonths : baseTotalContactLimit;
		const creditsSourceTitle = getContactLimitTitle(addon);
		const finalContactLimit = baseTotalContactLimit + addCredits;
		const statusBadge = getAddonStatusBadge(getExistingAddonStatus(addon?.code));

		return (
			<div key={addon._id} className="relative rounded-3xl border-2 border-gray-100 bg-white transition-all duration-300 flex flex-col h-full hover:border-[#503691]/30 hover:shadow-lg overflow-hidden group">
				{/* Top Header */}
				<div className="p-5 border-b border-gray-50 flex-1">
					<div className="flex items-start justify-between mb-4">
						<div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
							{isCreditAddon ? <Zap size={24} /> : <ShieldCheck size={24} />}
						</div>
						{statusBadge && (
							<span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${statusBadge.className}`}>
								{statusBadge.label}
							</span>
						)}
					</div>
					
					<h3 className="text-xl font-black text-gray-900 leading-tight mb-2 capitalize">{addon.name}</h3>
					<p className="text-sm text-gray-500 font-medium leading-relaxed mb-4">
						{addon.description || `Unlock premium ${addon.name} capabilities for your business account.`}
					</p>

					{/* Metric Info Section */}
					<div className="space-y-3 bg-gray-50/50 p-4 rounded-2xl border border-gray-100">
						{(baseTotalContactLimit > 0 || addCredits > 0) && (
							<div className="flex flex-col gap-1">
								<span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Contact Limit</span>
								<div className="flex items-center gap-2">
									<div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
									<span className="text-sm font-black text-gray-900">
										{isCreditAddon ? (
											`${creditsSourceTitle}: ${formatMetricValue(baseTotalContactLimit)} + ${formatMetricValue(addCredits)}`
										) : (
											`${creditsSourceTitle}: ${formatMetricValue(baseTotalContactLimit)}`
										)}
									</span>
								</div>
								{showDuration && (
									<p className="text-[11px] font-bold text-gray-500 ml-3.5">
										Total: {formatMetricValue(scaledTotalContactLimit)} credits
									</p>
								)}
							</div>
						)}
						
						{showDuration && (
							<div className="flex flex-col gap-2 pt-1">
								<span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Duration</span>
								<button
									onClick={() => setActiveDurationPopup(addon._id)}
									className="flex items-center justify-between w-full px-4 py-2.5 rounded-xl bg-white border border-gray-200 hover:border-[#503691] transition-all text-sm font-black text-gray-900"
								>
									<span className="flex items-center gap-2">
										<Clock size={16} className="text-[#503691]" />
										{selectedDurationMonths} {selectedDurationMonths === 1 ? "Month" : "Months"}
									</span>
									<ChevronDown size={16} className="text-gray-400" />
								</button>
								<DurationPopup
									isOpen={activeDurationPopup === addon._id}
									onClose={() => setActiveDurationPopup(null)}
									selected={selectedDurationMonths}
									onSelect={(months) => setFeatureDurations((prev) => ({ ...prev, [addon._id]: months }))}
								/>
							</div>
						)}
					</div>
				</div>

				{/* Footer Price & Button */}
				<div className="p-5 bg-white border-t border-gray-50 mt-auto">
					<div className="flex items-end justify-between mb-4">
						<div>
							<p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Price</p>
							<div className="flex items-baseline gap-1">
								<span className="text-2xl font-black text-gray-900">₹{formatMetricValue(featureCost)}</span>
								<span className="text-xs font-bold text-gray-500">
									/{showDuration ? `${selectedDurationMonths}mo` : "one-time"}
								</span>
							</div>
						</div>
					</div>

					<button
						onClick={() => startAddonCheckout({ addon, accessKey, value: 1, durationMonths: showDuration ? selectedDurationMonths : 1 })}
						disabled={processingAddonId === addon._id}
						className="w-full py-3.5 rounded-2xl bg-[#503691] text-white font-black text-sm tracking-wide shadow-lg shadow-[#503691]/20 transition-all hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:shadow-none"
					>
						{processingAddonId === addon._id ? "Processing..." : "Add Your Plan"}
					</button>
				</div>
			</div>
		);
	};

	return (
		<div className="max-w-7xl mx-auto px-4">
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
				{mappedAddons.map(({ addon, accessKey, isSubUser }) =>
					isSubUser ? renderSubUserCard(addon, accessKey) : renderNormalCard(addon, accessKey)
				)}
			</div>
		</div>
	);
};

export default Addons;
